import React, { useState, useEffect } from 'react';
import { useProjects } from '../../lib/ProjectContext';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/button';
import { getUserEmailById } from '../../lib/firebase';
import { fuzzySearch } from '../../lib/utils';

interface ProjectListProps {
  onOpenProject: (projectId: string) => Promise<void>;
  searchQuery?: string;
}

const ProjectList: React.FC<ProjectListProps> = ({ onOpenProject, searchQuery = '' }) => {
  const { projects, loading, deleteProject, createNewProject, isCurrentUserOwner, updateProject, syncWithFirebase } = useProjects();
  const { currentUser } = useAuth();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [userEmails, setUserEmails] = useState<{[key: string]: string}>({});
  const [showArchived, setShowArchived] = useState(false);

  // Function to get user emails from Firebase Auth
  useEffect(() => {
    const fetchUserEmails = async () => {
      const userIds = projects
        .filter(project => project.userId && !project.userEmail)
        .map(project => project.userId);
      
      // Remove duplicates
      const uniqueUserIds = Array.from(new Set(userIds));
      
      if (uniqueUserIds.length === 0) return;

      try {
        // Create a temporary mapping of userIds to emails
        const emailMap: {[key: string]: string} = {};
        
        // For each userId, try to find their email
        for (const userId of uniqueUserIds) {
          try {
            const email = await getUserEmailById(userId);
            if (email) {
              emailMap[userId] = email;
            }
          } catch (error) {
            console.error(`Error fetching user data for ${userId}:`, error);
          }
        }
        
        setUserEmails(emailMap);
      } catch (error) {
        console.error('Error fetching user emails:', error);
      }
    };

    if (projects.length > 0) {
      fetchUserEmails();
    }
  }, [projects]);

  const handleShareProject = (projectId: string, slug?: string) => {
    if (!slug) return;
    
    // Create the shareable URL with the project slug
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}?project=${slug}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        setCopiedId(projectId);
        setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
      },
      (err) => console.error('Could not copy URL: ', err)
    );
  };

  const handleArchiveProject = async (projectId: string) => {
    if (!projectId) return;
    try {
      await updateProject(projectId, { archived: true });
    } catch (error) {
      console.error('Error archiving project:', error);
    }
  };

  const handleRestoreProject = async (projectId: string) => {
    if (!projectId) return;
    try {
      await updateProject(projectId, { archived: false });
    } catch (error) {
      console.error('Error restoring project:', error);
    }
  };
  
  const handleToggleApproval = async (projectId: string, approved: boolean) => {
    if (!projectId) return;
    try {
      // If approving, make sure to remove rejected status
      if (approved) {
        await updateProject(projectId, { approved, rejected: false });
      } else {
        await updateProject(projectId, { approved });
      }
      await syncWithFirebase();
    } catch (error) {
      console.error('Error toggling project approval:', error);
    }
  };
  
  const handleToggleRejection = async (projectId: string, rejected: boolean) => {
    if (!projectId) return;
    try {
      // If rejecting, make sure to remove approved status
      if (rejected) {
        await updateProject(projectId, { rejected, approved: false });
      } else {
        await updateProject(projectId, { rejected });
      }
      await syncWithFirebase();
    } catch (error) {
      console.error('Error toggling project rejection:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading projects...</div>;
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          No saved estimates available yet. Create your first estimate to get started.
        </p>
      </div>
    );
  }

  // Helper function to get user email
  const getUserEmail = (project: any): string => {
    // First check if the project already has a userEmail
    if (project.userEmail) {
      return project.userEmail;
    }
    
    // Then check our cached emails
    if (userEmails[project.userId]) {
      return userEmails[project.userId];
    }
    
    // If it's the current user's project, use their email
    if (currentUser && project.userId === currentUser.uid && currentUser.email) {
      return currentUser.email;
    }
    
    // Fetch the email asynchronously (will update on next render)
    getUserEmailById(project.userId).then(email => {
      if (email) {
        setUserEmails(prev => ({
          ...prev,
          [project.userId]: email
        }));
      }
    }).catch(err => console.error('Error fetching email:', err));
    
    return 'Unknown user';
  };

  // Format date to show date and time
  const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  // Calculate relative time (e.g., "2 hours ago")
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  };
  
  // Calculate the total hours estimate range for a project
  const calculateEstimateRange = (project: any): string => {
    // Calculate feature hours
    const featuresMinHours = project.selectedFeatures.reduce((sum: number, feature: any) => sum + feature.minHours, 0);
    const featuresMaxHours = project.selectedFeatures.reduce((sum: number, feature: any) => sum + feature.maxHours, 0);
    
    // Calculate component hours
    const componentsHours = project.componentCount * 3;
    
    // Calculate migration hours
    const migrationHours = project.isMigration ? (project.postTypeCount || 0) * 2 : 0;
    
    // Calculate content upload hours
    const getContentUploadHours = () => {
      if (!project.isContentUpload) return 0;
      switch (project.contentUploadLevel) {
        case 'light': return 4;
        case 'medium': return 8;
        case 'heavy': return 12;
        default: return 0;
      }
    };
    
    const contentUploadHours = getContentUploadHours();
    
    // Calculate total hours
    const totalMinHours = featuresMinHours + componentsHours + migrationHours + contentUploadHours;
    const totalMaxHours = featuresMaxHours + componentsHours + migrationHours + contentUploadHours;
    
    // Format the output
    return totalMinHours === totalMaxHours 
      ? `${totalMinHours} hours` 
      : `${totalMinHours}-${totalMaxHours} hours`;
  };

  const renderProjectItem = (project: any) => {
    const isShared = currentUser && project.userId !== currentUser.uid;
    const isInArchivedSection = project.archived;
    
    return (
      <div 
        key={project.id} 
        className="border rounded-lg p-4 transition-colors cursor-pointer hover:border-primary"
        onClick={() => project.id && onOpenProject(project.id)}
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium">{project.name}</h3>
              {isShared && (
                <Button variant="outline" size="sm" className="pointer-events-none h-7 text-xs px-2">Shared</Button>
              )}
              {project.approved && (
                <Button variant="outline" size="sm" className="pointer-events-none h-7 text-xs px-2 bg-green-100 text-green-800 border-green-200 hover:bg-green-100 hover:text-green-800">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                  Approved
                </Button>
              )}
              {project.rejected && (
                <Button variant="outline" size="sm" className="pointer-events-none h-7 text-xs px-2 bg-red-100 text-red-800 border-red-200 hover:bg-red-100 hover:text-red-800">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Rejected
                </Button>
              )}
            </div>
            <p className={`text-sm text-muted-foreground`}>
              {project.description}
            </p>
            <div className="text-sm mt-1 font-medium">
              {calculateEstimateRange(project)}
            </div>
            <div className={`flex gap-2 mt-1 text-xs text-muted-foreground`}>
              <span>Components: {project.componentCount}</span>
              <span>Features: {project.selectedFeatures.length}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                project.id && onOpenProject(project.id);
              }}
            >
              Open
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleShareProject(project.id, project.slug);
              }}
              className="flex items-center gap-1"
            >
              {copiedId === project.id ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  Share
                </>
              )}
            </Button>
            
            {project.archived ? (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRestoreProject(project.id);
                }}
              >
                Restore
              </Button>
            ) : (
              <>
                <Button
                  variant={project.approved ? "outline" : "secondary"}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleApproval(project.id, !project.approved);
                  }}
                >
                  {project.approved ? 'Unapprove' : 'Approve'}
                </Button>
                <Button
                  variant={project.rejected ? "outline" : "secondary"}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleRejection(project.id, !project.rejected);
                  }}
                >
                  {project.rejected ? 'Unreject' : 'Reject'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchiveProject(project.id);
                  }}
                >
                  Archive
                </Button>
              </>
            )}
            
            <Button 
              variant="destructive" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (project.id && window.confirm('Are you sure you want to delete this project?')) {
                  deleteProject(project.id);
                }
              }}
            >
              Delete
            </Button>
          </div>
        </div>
        <div className="flex justify-between text-xs mt-2 text-muted-foreground">
          <div>
            <span title={formatDateTime(project.updatedAt)}>
              Updated: {getRelativeTime(project.updatedAt)}
            </span>
          </div>
          {isShared && <span>Created by: {getUserEmail(project)}</span>}
        </div>
      </div>
    );
  };

  // Sort all projects by updatedAt (most recent first)
  const sortedProjects = [...projects].sort((a, b) => 
    b.updatedAt.getTime() - a.updatedAt.getTime()
  );

  // Filter projects based on search query
  const filterProjects = (projectList: any[]) => {
    if (!searchQuery) return projectList;
    
    const query = searchQuery.toLowerCase().trim();
    return projectList.filter(project => {
      // Search in name, description, and features
      const nameMatch = fuzzySearch(project.name, query);
      const descMatch = project.description ? fuzzySearch(project.description, query) : false;
      
      // Search in feature names
      const featureMatch = project.selectedFeatures.some((feature: any) => 
        fuzzySearch(feature.name, query)
      );
      
      return nameMatch || descMatch || featureMatch;
    });
  };

  // Separate active and archived projects
  const activeProjects = filterProjects(sortedProjects.filter(project => !project.archived));
  const archivedProjects = filterProjects(sortedProjects.filter(project => project.archived));

  return (
    <div className="space-y-6">
      {/* Active Projects */}
      <div className="space-y-4">
        {activeProjects.length > 0 ? (
          activeProjects.map(project => renderProjectItem(project))
        ) : (
          <div className="text-center py-4">
            <p className="text-muted-foreground">
              {searchQuery ? 'No matching projects found.' : 'No active estimates available.'}
            </p>
          </div>
        )}
      </div>

      {/* Archived Projects Accordion */}
      {archivedProjects.length > 0 && (
        <div className="border rounded-lg overflow-hidden border">
          <button
            className="w-full p-4 text-left font-medium flex justify-between items-center bg-black text-white transition-colors"
            onClick={() => setShowArchived(!showArchived)}
          >
            <span>Archived Estimates ({archivedProjects.length})</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform ${showArchived ? 'rotate-180' : ''}`}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          
          {showArchived && (
            <div className="p-4 space-y-4">
              {archivedProjects.map(project => renderProjectItem(project))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectList; 